/** @jsxImportSource @emotion/react */

import {
  AvailableOptions,
  LastFiredState,
  ValidationErrors,
} from "components/AutocompleteInput";
import { Button } from "components/Button";
import { Card, CardHeader } from "components/Card";
import {
  NOTIFICATION_TYPE,
  NotificationContext,
} from "context/NotificationContext";
import { UserContext } from "context/UserContext";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import {
  ADDLRequest,
  AliRecord,
  ALIRecordRequest,
  getDefaultALIRecordRequest,
} from "services/ALIRecord.d";
import {
  AliChange,
  createAliChangeDiscrepancyFromAliRecord,
} from "services/DiscrepancyType";
import { Msag } from "services/MSAGRecord";
import RecordService from "services/RecordService";
import {
  formatHouseNumber,
  formatPhoneNumber,
  formatZipCode,
  unformatPhoneNumber,
} from "utils/helpers";
import { ALIChangeRequestView } from "./SubViews/ALIChangeRequestViews";
import { ALIWriteViews } from "./SubViews/ALIWriteViews";

// Service instances
const recordService = new RecordService("ali");
const msagClient = new RecordService("msag");
const discrepClient = new RecordService("discrep");

export interface ALIViewProps {
  recordRequest: ALIRecordRequest;
  setRecordRequest: React.Dispatch<React.SetStateAction<ALIRecordRequest>>;
  record: AliRecord;
  handleChange: (e: any) => void;
  hasAddlDataPermission: boolean | undefined;
  addlDataRequest: ADDLRequest;
  setAddlDataRequest: React.Dispatch<React.SetStateAction<ADDLRequest>>;
  lastFired: LastFiredState;
  lastMSAGRecord: Msag | null;
  setLastMSAGRecord: React.Dispatch<React.SetStateAction<Msag | null>>;
  availableOptions: AvailableOptions;
  setAvailableOptions: React.Dispatch<React.SetStateAction<AvailableOptions>>;
  validationErrors: ValidationErrors;
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  mode: "create" | "edit" | "migrate";
}

// Utility functions
const getAddressCriterias = (recordRequest: ALIRecordRequest | AliChange) => {
  const criterias: Array<{ field: string; type: string; value: string }> = [];
  if ("MCN" in recordRequest && recordRequest.MCN) {
    if ("STA" in recordRequest && recordRequest.STA) {
      criterias.push({
        field: "State",
        type: "like",
        value: recordRequest.STA,
      });
    }
    criterias.push({
      field: "MCN",
      type: "like",
      value: recordRequest.MCN,
    });
  }
  if ("STN" in recordRequest && recordRequest.STN) {
    criterias.push({
      field: "StreetName",
      type: "like",
      value: recordRequest.STN,
    });
  }
  if ("STS" in recordRequest && recordRequest.STS) {
    criterias.push({
      field: "StreetSuffix",
      type: "like",
      value: recordRequest.STS,
    });
  }

  return criterias;
};

const getAutoCompleteValuesFromRequest = (
  field: string,
  recordRequest: ALIRecordRequest | AliChange,
) => {
  let msag_fields = {
    MCN: (recordRequest as any).MCN || (recordRequest as any)["EDIT_MCN"] || "",
    StreetName:
      (recordRequest as any).STN || (recordRequest as any)["EDIT_STN"] || "",
    StreetSuffix:
      (recordRequest as any).STS || (recordRequest as any)["EDIT_STS"] || "",
    State:
      (recordRequest as any).STA || (recordRequest as any)["EDIT_STA"] || "",
  };

  let currValue = msag_fields[field];
  Object.keys(msag_fields).forEach((key) => {
    if (key === field) delete msag_fields[key];
    if (!msag_fields[key]) delete msag_fields[key];
  });

  let request = {
    autocomplete_field: field,
    autocomplete_value: currValue,
    msag_fields,
    Full: true,
  };

  return request;
};

export function getOptionsGeneric(
  field: string,
  recordRequest: ALIRecordRequest | AliChange,
  setLastMSAGRecord?: React.Dispatch<React.SetStateAction<Msag | null>>,
): Promise<any> {
  return new Promise(function (resolve) {
    if (setLastMSAGRecord) {
      const crits = getAddressCriterias(recordRequest);
      msagClient
        .getRecords(
          {
            criterias: crits,
            range: "0,25",
          },
          "search",
        )
        .then((res) => {
          if (res.data.message.List) {
            console.log("Fetched Last MSAG record:");
            if (res.data.message.List.length === 1 && setLastMSAGRecord)
              setLastMSAGRecord(res.data.message.List[0] as Msag);
            else if (setLastMSAGRecord)
              setLastMSAGRecord(res.data.message.List);
          }
        })
        .catch(() => {
          console.log("Unable to fetch last MSAG record");
        });
    }

    const msagAutocompleteReq = getAutoCompleteValuesFromRequest(
      field,
      recordRequest,
    );

    msagClient
      .getAutocompleteOptions(msagAutocompleteReq)
      .then((res) => {
        if (res.list) resolve(res.list);
        else resolve([]);
      })
      .catch(() => {
        resolve([]);
      });
  });
}
interface ALIFormProps {
  mode: "create" | "edit" | "migrate";
  initialRecord?: AliRecord;
}

function ALIForm({ mode, initialRecord }: ALIFormProps) {
  const { addNotification } = useContext(NotificationContext);
  const { user } = useContext(UserContext);

  // Permission checks
  const hasWritePermission = user?.functions?.includes("foresite:ali:write");
  const hasChangeRequestPermission = user?.functions?.includes(
    "foresite:ali:requestChange",
  );
  const hasAddlDataPermission = user?.functions?.includes(
    "foresite:ali:addlData:write",
  );

  const location = useLocation();

  const loaderData = useLoaderData() as AliRecord;
  const record = useMemo(() => {
    const state = location.state as { CPN?: string; CPF?: string } | undefined;
    const defaultRecord = getDefaultALIRecordRequest() as AliRecord;

    switch (mode) {
      case "migrate":
        return state?.CPN && state?.CPF
          ? { ...defaultRecord, CPN: state.CPN, CPF: state.CPF }
          : defaultRecord;

      case "create":
        return state?.CPN
          ? {
              ...defaultRecord,
              CPN: state.CPN,
              MainNPA: unformatPhoneNumber(state.CPN).substring(0, 3),
              MainNumber: unformatPhoneNumber(state.CPN).substring(3),
            }
          : defaultRecord;

      case "edit":
        return initialRecord || loaderData;

      default:
        return defaultRecord;
    }
  }, [location.state, mode, initialRecord, loaderData]);

  const navigate = useNavigate();

  const getFOCByMode = (
    mode: "create" | "edit" | "migrate",
    recordFOC?: string,
  ) => {
    const focMap = {
      migrate: "M",
      create: "I",
      edit: "C",
    };

    return focMap[mode] || recordFOC;
  };

  const initialFOC = getFOCByMode(mode, record?.FOC);

  const [recordRequest, setRecordRequest] = useState<ALIRecordRequest>({
    CPN: formatPhoneNumber(record.CPN || "") || "",
    FOC: String(initialFOC ?? record.FOC ?? ""),
    NAM: record.NAM || "",
    STA: record.STA || "",
    MCN: record.MCN || "",
    STN: record.STN || "",
    STS: record.STS || "",
    POD: record.POD || "",
    HNO: formatHouseNumber(record, record.HNO) || "",
    HNS: record.HNS || "",
    LOC: record.LOC || "",
    CPF: record.CPF || "",
    CLS: record.CLS || "",
    TYS: record.TYS || "",
    Comments: record.Comments || "",
    lecZipCode: formatZipCode(record.ZIP || "") || "",
    lecZipExtension: record.lecZipExtension || "",
    lecESN: record.lecESN || "",
    MainNPA: record.MainNPA || "",
    MainNumber: record.MainNumber || "",
    Exchange: record.Exchange || "",
    OrderNumber: record.OrderNumber || "",
    lecLAT: record.lecLAT || "",
    lecLON: record.lecLON || "",
    lecELV: record.lecELV || "",
    NENAReserved: record.NENAReserved || "",
    DataProviderID: record.DataProviderID || "",
    Reserved2: record.Reserved2 || "",
    GeneralUse: record.GeneralUse || "",
  } as ALIRecordRequest);

  const [discrepancyRequest, setDiscrepancyRequest] = useState<AliChange>(
    createAliChangeDiscrepancyFromAliRecord(recordRequest || {}),
  );

  const [addlDataRequest, setAddlDataRequest] = useState<ADDLRequest>({
    privateCit: record.privateCit || false,
    dd: record.dd || 0,
  });

  const [isSaving, setIsSaving] = useState<boolean | undefined>(undefined);
  const [editedDiscrepancyFields, setEditedDiscrepancyFields] = useState<
    string[]
  >([]);
  const [editedAddlDataFields, setEditedAddlDataFields] = useState<string[]>(
    [],
  );

  const [lastMSAGRecord, setLastMSAGRecord] = React.useState<Msag | null>(null);

  // if form is create or migrate, set the lecCOI field to lastMSAGRecord.CountyID
  useEffect(() => {
    if (mode === "create" || mode === "migrate") {
      const countyID = Array.isArray(lastMSAGRecord)
        ? lastMSAGRecord[0]?.CountyID
        : lastMSAGRecord?.CountyID;
      setRecordRequest((prev) => ({
        ...prev,
        lecCOI: countyID || "",
      }));
    }
  }, [mode, lastMSAGRecord]);

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [availableOptions, setAvailableOptions] = useState<AvailableOptions>({
    MCN: [],
    STN: [],
    STA: [],
    STS: [],
  });
  const [lastFired, setLastFired] = useState<LastFiredState>({
    MCN: new Date().toString(),
    STN: new Date().toString(),
    STS: new Date().toString(),
    STA: new Date().toString(),
  });

  // Memoized functions
  const getEditedFields = useCallback(() => {
    if (mode === "create") return;

    const discrepancyFields: string[] = [];
    const addlFields: string[] = [];

    // make sure to check differences between null and empty string
    Object.keys(recordRequest).forEach((key) => {
      // if its CPN field, compare unformatted values
      if (key === "CPN") {
        const recordValue = unformatPhoneNumber(recordRequest.CPN || "");
        const originalValue = unformatPhoneNumber(record.CPN || "");
        if (recordValue !== originalValue) discrepancyFields.push(key);
        return;
      }

      if (recordRequest[key] !== record[key]) {
        const recordValue = record[key] === null ? "" : record[key];
        const requestValue =
          recordRequest[key] === null ? "" : recordRequest[key];
        if (recordValue !== requestValue) discrepancyFields.push(key);
      }
    });

    Object.keys(addlDataRequest).forEach((key) => {
      if (addlDataRequest[key] !== record[key]) {
        const recordValue = record[key] === null ? "" : record[key];
        const requestValue =
          addlDataRequest[key] === null ? "" : addlDataRequest[key];
        if (recordValue !== requestValue) addlFields.push(key);
      }
    });

    if (
      JSON.stringify(discrepancyFields.sort()) !==
      JSON.stringify(editedDiscrepancyFields.sort())
    ) {
      setEditedDiscrepancyFields(discrepancyFields);
    }

    if (
      JSON.stringify(addlFields.sort()) !==
      JSON.stringify(editedAddlDataFields.sort())
    ) {
      setEditedAddlDataFields(addlFields);
    }
  }, [
    recordRequest,
    addlDataRequest,
    record,
    editedDiscrepancyFields,
    editedAddlDataFields,
    mode,
  ]);

  // Effects
  useEffect(() => {
    setDiscrepancyRequest(
      createAliChangeDiscrepancyFromAliRecord(recordRequest || {}),
    );
    getEditedFields();
  }, [recordRequest, addlDataRequest, getEditedFields]);

  useEffect(() => {
    setLastFired({
      MCN: new Date().toString(),
      STN: new Date().toString(),
      STS: new Date().toString(),
      STA: new Date().toString(),
    });
  }, [
    recordRequest.MCN,
    recordRequest.STN,
    recordRequest.STS,
    recordRequest.STA,
  ]);

  // Event handlers
  const handleChange = (e: any) => {
    const { name, value } = e.target as
      | HTMLCalciteInputElement
      | HTMLCalciteTextAreaElement;
    setRecordRequest({
      ...recordRequest,
      [name]: value,
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const promises: Promise<any>[] = [];

      recordRequest.CPN = unformatPhoneNumber(recordRequest.CPN || "");

      if (mode === "create") {
        promises.push(
          recordService.addAliRecord(
            { ...recordRequest, CPN: unformatPhoneNumber(recordRequest.CPN) },
            "insert",
          ),
        );
      } else if (mode === "migrate") {
        promises.push(
          recordService.migrateCPN(
            unformatPhoneNumber(recordRequest.CPN),
            recordRequest.CPF || record.CPF || "",
            "migrateCPN",
            recordRequest,
          ),
        );
      } else {
        // Edit mode
        const editedDiscrepancy = editedDiscrepancyFields.length > 0;
        const editedAddlData = editedAddlDataFields.length > 0;

        const shouldUpdateRecord = hasWritePermission && editedDiscrepancy;
        const shouldUpdateAddlData = hasAddlDataPermission && editedAddlData;
        const shouldCreateChangeRequest =
          hasChangeRequestPermission &&
          !hasWritePermission &&
          editedDiscrepancy;

        console.log(
          shouldCreateChangeRequest,
          editedDiscrepancy,
          editedDiscrepancyFields,
        );

        if (shouldUpdateRecord) {
          const updatedRecordRequest: Partial<ALIRecordRequest> = {};
          editedDiscrepancyFields.forEach((field) => {
            updatedRecordRequest[field as keyof ALIRecordRequest] =
              recordRequest[field as keyof ALIRecordRequest];
          });
          promises.push(
            recordService.updateAliRecord(
              unformatPhoneNumber(recordRequest.CPN),
              updatedRecordRequest,
              "update",
            ),
          );
        }

        if (shouldUpdateAddlData)
          promises.push(
            recordService.updateAliRecord(
              unformatPhoneNumber(recordRequest.CPN),
              {
                privateCit: addlDataRequest.privateCit ? "1" : "0",
                dd: String(addlDataRequest.dd),
                STA: recordRequest.STA,
              },
              "update/addlData",
            ),
          );

        if (shouldCreateChangeRequest) {
          const notes = discrepancyRequest.NOTES || "";
          delete discrepancyRequest.NOTES;

          promises.push(
            discrepClient.insertChangeRequestDiscrepancy(
              record,
              {
                ...discrepancyRequest,
                CPN: unformatPhoneNumber(discrepancyRequest.CPN || ""),
              },
              notes
                ? { note: notes, submitted_by: user?.username || "" }
                : undefined,
            ),
          );
        }
      }

      const responses = await Promise.all(promises);

      const getSuccessMessage = (responses: any[]) => {
        if (mode === "create") return "ALI Record successfully created!";
        if (mode === "migrate") return "ALI Record successfully migrated!";

        const successMessages: string[] = [];
        const editedDiscrepancy = editedDiscrepancyFields.length > 0;
        const editedAddlData = editedAddlDataFields.length > 0;

        if (hasWritePermission && editedDiscrepancy)
          successMessages.push("ALI Record successfully updated");
        else if (
          hasChangeRequestPermission &&
          !hasWritePermission &&
          editedDiscrepancy
        ) {
          // if it creates a dicrepancy, the id will be in the response, display it in the success message
          const changeRequestId = responses.find((res) => res.data?.message?.id)
            ?.data?.message?.id;
          if (changeRequestId)
            successMessages.push(
              `Discrepancy ${changeRequestId} created for ALI Record change request`,
            );
          else successMessages.push("ALI Record change request submitted");
        }

        if (hasAddlDataPermission && editedAddlData)
          successMessages.push(
            "Disability Designations and/or PrivateCit values have been updated",
          );

        return successMessages.length > 0
          ? `${successMessages.join(" and ")}!`
          : "Successfully made changes!";
      };

      if (responses.every((res) => res.status === 200)) {
        console.log(responses);
        addNotification({
          title: "Success!",
          message: getSuccessMessage(responses),
          type: NOTIFICATION_TYPE.SUCCESS,
        });
        setIsSaving(undefined);
        setTimeout(() => {
          navigate("/ali-records");
        }, 1500);
      }
    } catch (error: any) {
      setIsSaving(undefined);
      addNotification({
        title: "Uh oh.",
        message: "Something went wrong: " + error?.message,
        type: NOTIFICATION_TYPE.ERROR,
      });
    }
  };

  const validateRequest = () => {
    const requiredFields = [
      "CPN",
      "NAM",
      "STA",
      "MCN",
      "STN",
      "HNO",
      "CPF",
      "CLS",
      "TYS",
    ];

    return requiredFields.some(
      (field) => !recordRequest[field as keyof typeof recordRequest],
    );
  };

  const getHeaderText = () => {
    switch (mode) {
      case "create":
        return "Create ALI Record";
      case "migrate":
        return "Migrate ALI Record";
      case "edit":
        return "Edit ALI Record";
      default:
        return "ALI Record";
    }
  };

  const isFormDisabled = () => {
    if (mode === "create" || mode === "migrate") {
      return validateRequest();
    }
    return (
      isSaving ||
      (editedDiscrepancyFields.length === 0 &&
        editedAddlDataFields.length === 0) ||
      validateRequest()
    );
  };

  if (mode !== "create" && !record) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <CardHeader>{getHeaderText()}</CardHeader>
      <Card style={{ width: "100%", marginBottom: "20px" }}>
        <form onSubmit={handleSubmit} id="aliRecord">
          {(hasWritePermission || mode === "create" || mode === "migrate") && (
            <ALIWriteViews
              recordRequest={recordRequest}
              setRecordRequest={setRecordRequest}
              record={record}
              handleChange={handleChange}
              hasAddlDataPermission={hasAddlDataPermission}
              addlDataRequest={addlDataRequest}
              setAddlDataRequest={setAddlDataRequest}
              lastFired={lastFired}
              lastMSAGRecord={lastMSAGRecord}
              setLastMSAGRecord={setLastMSAGRecord}
              availableOptions={availableOptions}
              setAvailableOptions={setAvailableOptions}
              validationErrors={validationErrors}
              setValidationErrors={setValidationErrors}
              mode={mode}
            />
          )}

          {hasChangeRequestPermission &&
            !hasWritePermission &&
            mode === "edit" && (
              <ALIChangeRequestView
                recordRequest={recordRequest}
                setRecordRequest={setRecordRequest}
                record={record}
                handleChange={handleChange}
                hasAddlDataPermission={hasAddlDataPermission}
                addlDataRequest={addlDataRequest}
                setAddlDataRequest={setAddlDataRequest}
                lastFired={lastFired}
                lastMSAGRecord={lastMSAGRecord}
                mode={mode}
                setLastMSAGRecord={setLastMSAGRecord}
                availableOptions={availableOptions}
                setAvailableOptions={setAvailableOptions}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
              />
            )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
              gap: "12px",
            }}
          >
            <Button
              variant="outline"
              kind="brand"
              label="Cancel"
              onClick={() => navigate(-1)}
              type="button"
            >
              Cancel
            </Button>
            {(hasWritePermission ||
              hasChangeRequestPermission ||
              mode === "create" ||
              mode === "migrate") && (
              <Button
                isLoading={isSaving}
                disabled={isFormDisabled()}
                form="aliRecord"
                type="submit"
                width="auto"
              >
                {mode === "create"
                  ? "Create"
                  : mode === "migrate"
                    ? "Migrate"
                    : "Save"}
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
}

export function ALICreate() {
  return <ALIForm mode="create" />;
}

export function ALIEdit({ initialRecord }: { initialRecord?: AliRecord }) {
  return <ALIForm mode="edit" initialRecord={initialRecord} />;
}

export function ALIMigrate({ initialRecord }: { initialRecord?: AliRecord }) {
  return <ALIForm mode="migrate" initialRecord={initialRecord} />;
}

export default ALIEdit;
